import { useHistory } from 'react-router-dom';
import * as React from 'react';

import { LodgingOnSubmitCallback, SearchForm, SearchFormInputs, useLodgingURLParamsToQueryInputs } from '@shared-ui/search-tools-lodging';

import { HOUSE_RULES_GROUP } from 'src/common/constants/filters';

import { EssClientLogger } from 'typeahead-client-logger';
import { parse } from 'query-string';

interface SearchFormWrappingProps {
  handleSubmit: LodgingOnSubmitCallback;
  essLogger: EssClientLogger;
  showDatePicker: boolean;
  autoOpenPlayback: boolean;
  stickySearchForm: boolean;
  showWeekday: boolean;
  toggleDatePicker: (shouldOpen: boolean) => void;
  dateLocalFormat?: string;
  queryParamStartDate: string;
  queryParamEndDate: string;
  queryParamLatLong?: string;
  alternateDestination: boolean;
  dateFieldInitialFocus?: boolean;
}

const getDate = (date: string, dateLocalFormat: string) => {
  if (dateLocalFormat === 'd/M/yyyy' && /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/.test(date)) {
    const dateParts = date.split('/');

    return {
      year: parseInt(dateParts[2], 10),
      month: parseInt(dateParts[1], 10),
      day: parseInt(dateParts[0], 10),
    };
  }

  if (dateLocalFormat === 'M/d/yyyy' && /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(date)) {
    const dateParts = date.split('/');

    return {
      year: parseInt(dateParts[2], 10),
      month: parseInt(dateParts[0], 10),
      day: parseInt(dateParts[1], 10),
    };
  }

  return null;
};

const getFinalDateInputs = (
  inputs: SearchFormInputs | null,
  inputStartDate: { year: number; month: number; day: number },
  inputEndDate: { year: number; month: number; day: number }
) => {
  if (inputs?.propertySearchCriteriaInput) {
    return {
      ...inputs,
      propertySearchCriteriaInput: {
        ...inputs?.propertySearchCriteriaInput,
        primary: {
          ...inputs.propertySearchCriteriaInput.primary,
          dateRange: {
            ...inputs?.propertySearchCriteriaInput?.primary.dateRange,
            checkInDate: inputStartDate,
            checkOutDate: inputEndDate,
          },
        },
      },
    };
  }

  return inputs;
};

export const SearchFormWrapping = React.forwardRef<HTMLDivElement, SearchFormWrappingProps>(
  (
    {
      handleSubmit,
      essLogger,
      showDatePicker,
      toggleDatePicker,
      autoOpenPlayback,
      stickySearchForm,
      showWeekday,
      dateLocalFormat,
      queryParamStartDate,
      queryParamEndDate,
      queryParamLatLong,
      alternateDestination,
      dateFieldInitialFocus,
    },
    searchFormRef
  ) => {
    const inputs = useLodgingURLParamsToQueryInputs();
    const inputStartDate = getDate(queryParamStartDate, dateLocalFormat ?? '');
    const inputEndDate = getDate(queryParamEndDate, dateLocalFormat ?? '');
    const finalInputs = inputStartDate && inputEndDate ? getFinalDateInputs(inputs, inputStartDate, inputEndDate) : inputs;

    const history = useHistory();
    const [skipQuery, setSkipQuery] = React.useState(false);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const isAnyPetAllowed = inputs?.propertySearchCriteriaInput?.secondary?.selections?.some(
      selection => selection.value === HOUSE_RULES_GROUP.PETS_ALLOWED
    );

    React.useEffect(() => {
      /* istanbul ignore next */
      if (history.action === 'POP' && !isDialogOpen) {
        setSkipQuery(false);
      } else if (inputs && !skipQuery) {
        setSkipQuery(true);
      }
      setIsDialogOpen(Boolean(parse(history.location.search).pwaDialog));
    }, [inputs, skipQuery, history.action]);

    const testId = `${stickySearchForm ? 'sticky-' : ''}search-form1${showWeekday ? '-with-showWeekday' : ''}${
      alternateDestination ? '-with-alternateDestination' : ''
    }`;

    return (
      <div data-stid={testId} ref={searchFormRef}>
        <SearchForm
          inputs={finalInputs ?? {}}
          onSubmit={handleSubmit}
          includePlayback
          autoOpenPlayback={autoOpenPlayback}
          skipQuery={skipQuery}
          essLogger={essLogger}
          showCalendar={showDatePicker}
          toggleCalendar={toggleDatePicker}
          autoSubmitOnFieldChange
          isAnyPetAllowed={isAnyPetAllowed}
          sticky={stickySearchForm}
          showWeekday={showWeekday}
          additionalAdapterFields={alternateDestination ? { alternate: true, lastSearchLatLong: queryParamLatLong } : {}}
          dateFieldInitialFocus={dateFieldInitialFocus}
        />
      </div>
    );
  }
);

SearchFormWrapping.displayName = 'SearchFormWrapping';
